import { useUnit } from 'effector-react'
import type { InfiniteData } from 'react-query'
import { useMutation, useQueryClient } from 'react-query'

import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import type { CollectionsItem } from '@/dialogs/CollectionsDialog/CollectionsList/types'
import { $creatorId } from '@/models/creators.ts'
import { listsService } from '@/modules/chatting/service/lists'
import type { GetListsResponse } from '@/modules/chatting/types'
import { unescapeHtmlText } from '@/modules/chatting/ui/chat-window/fan-info/utils'
import { handleError } from '@/utils/error'

import { DialogProps } from '../types'

interface Props extends DialogProps {
  collection: CollectionsItem
  onDelete: () => void
}

export const DeleteCollectionConfirmationDialog = ({
  isOpen,
  onClose,
  onDelete,
  collection,
}: Props) => {
  const creatorId = useUnit($creatorId)
  const queryClient = useQueryClient()
  const deleteListMutation = useMutation(
    () =>
      listsService.deleteList({ listId: collection.id, creatorId: creatorId! }),
    {
      onSuccess: () => {
        queryClient.removeQueries(['listUsers', creatorId, collection.id])
        queryClient.setQueriesData<InfiniteData<GetListsResponse> | undefined>(
          'all-lists-extended',
          (oldData) => {
            if (!oldData) return oldData

            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                list: page.list.filter((l) => l.id !== collection.id),
              })),
            }
          },
        )
        onDelete()
        onClose()
      },
      onError: (error) => {
        handleError(error, 'Something went wrong while deleting list')
      },
    },
  )

  return (
    <Dialog
      bigDialog
      show={isOpen}
      onClose={onClose}
      panelClassName="lg:max-w-[533px]"
      showCloseIcon={false}
    >
      <div className="space-y-6">
        <Dialog.Header>
          <Dialog.Title>Delete List</Dialog.Title>
          <Dialog.Description className="text-center">
            Are you sure you want to delete {unescapeHtmlText(collection.name)}?
          </Dialog.Description>
        </Dialog.Header>
        <Dialog.ClosableFooter onClose={onClose}>
          <Button
            variant="destructive"
            isLoading={deleteListMutation.isLoading}
            onClick={() => deleteListMutation.mutate()}
          >
            Delete
          </Button>
        </Dialog.ClosableFooter>
      </div>
    </Dialog>
  )
}
